import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { CheckCircle, Edit, Save, Search } from "lucide-react";

function InfoCard({ label, value }) {
  return (
    <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
      <div className="text-2xl font-bold text-primary">{value}</div>
      <div className="text-xs text-gray-600">{label}</div>
    </div>
  );
}

function difficultyBadgeVariant(diff) {
  if (diff === "hard") return "destructive";
  if (diff === "medium") return "default";
  return "secondary";
}

export default function TestReviewPane({ test, onStartOver, onFinalize }) {
  const [query, setQuery] = useState("");
  const [difficultyFilter, setDifficultyFilter] = useState("all");

  const questions = test?.questions || [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return questions.filter((question) => {
      if (difficultyFilter !== "all" && question.difficulty !== difficultyFilter) return false;
      if (!q) return true;
      const text = `${question.question_text || ""} ${question.competency || ""}`.toLowerCase();
      return text.includes(q);
    });
  }, [questions, query, difficultyFilter]);

  if (!test) return null;

  const difficulties = [
    { id: "all", label: "All" },
    { id: "easy", label: "Easy" },
    { id: "medium", label: "Medium" },
    { id: "hard", label: "Hard" },
  ];

  return (
    <div className="space-y-6" data-testid="test-review-pane">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>{test.test_title}</CardTitle>
              <CardDescription>{test.test_description}</CardDescription>
            </div>
            <Badge className="bg-green-100 text-green-800 border-green-300">
              <CheckCircle className="w-3 h-3 mr-1" />
              AI Generated
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4 mb-6">
            <InfoCard label="Questions" value={questions.length} />
            <InfoCard label="Time Limit" value={`${test.time_limit_minutes} min`} />
            <InfoCard label="Passing Score" value={`${test.passing_score}%`} />
            <InfoCard label="Category" value={test.category} />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
            <h3 className="font-semibold text-lg flex-1">
              All Questions ({filtered.length} of {questions.length})
            </h3>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search questions…"
                className="pl-9 h-9"
                data-testid="review-search-input"
              />
            </div>
            <div className="flex gap-1 flex-wrap">
              {difficulties.map((d) => (
                <Button
                  key={d.id}
                  size="sm"
                  variant={difficultyFilter === d.id ? "default" : "outline"}
                  onClick={() => setDifficultyFilter(d.id)}
                  data-testid={`review-diff-${d.id}`}
                >
                  {d.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2" data-testid="review-questions-list">
            {filtered.map((q, idx) => (
              <div key={q.id || `q-${idx}`} className="p-4 border border-gray-200 rounded-lg bg-white" data-testid={`review-question-${idx}`}>
                <div className="flex items-start justify-between mb-2 gap-2">
                  <h4 className="font-medium text-gray-900">
                    Q{idx + 1}. {q.question_text}
                  </h4>
                  <div className="flex gap-1 flex-shrink-0">
                    <Badge variant={difficultyBadgeVariant(q.difficulty)}>{q.difficulty}</Badge>
                    {q.competency && <Badge variant="outline">{q.competency}</Badge>}
                  </div>
                </div>
                <div className="space-y-1 mt-2">
                  {(q.options || []).map((opt) => (
                    <div
                      key={`opt-${idx}-${opt.label}`}
                      className={`text-sm p-2 rounded ${
                        opt.label === q.correct_answer ? "bg-green-50 border border-green-200" : "bg-gray-50"
                      }`}
                    >
                      <span className="font-medium">{opt.label}.</span> {opt.text}
                      {opt.label === q.correct_answer && (
                        <CheckCircle className="w-3.5 h-3.5 inline-block ml-2 text-green-600" />
                      )}
                    </div>
                  ))}
                </div>
                {q.explanation && (
                  <p className="text-xs text-gray-600 mt-2 italic">✓ {q.explanation}</p>
                )}
              </div>
            ))}
            {filtered.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-8">No questions match your filter.</p>
            )}
          </div>

          <div className="flex gap-4 mt-8">
            <Button variant="outline" onClick={onStartOver} data-testid="start-over-btn">
              <Edit className="w-4 h-4 mr-2" />
              Start Over
            </Button>
            <Button
              onClick={onFinalize}
              className="flex-1 bg-primary hover:bg-primary/90 text-white"
              size="lg"
              data-testid="save-publish-test-btn"
            >
              <Save className="w-5 h-5 mr-2" />
              Save &amp; Publish Test
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
